/**
 * Treasury Payment Runs (UNIFIED) -- 2026-05-15
 *
 * Single canonical Payment Runs dashboard. Lists every run on the
 * platform regardless of source (vendor AP, employee reimbursement),
 * with a Source filter + per-row actions adapted to the run type.
 *
 * Replaces the legacy P2P PaymentRuns.tsx (deleted in this commit).
 *
 * Sources:
 *   VENDOR        -- AP vendor payment run (DRAFT -> PENDING_APPROVAL
 *                    -> APPROVED -> COMPLETED). Actions: submit /
 *                    approve / execute / NACHA download.
 *   REIMBURSEMENT -- employee expense reimbursement (PENDING ->
 *                    COMPLETED). Action: execute (Treasury disburse).
 *                    Lines surface bank link from D108 wire-up.
 *   MIXED         -- defensive bucket; shouldn't occur given how
 *                    runs are created.
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import StatusBadge from '../components/ui/StatusBadge';
import ActionButton from '../components/ui/ActionButton';
import Modal from '../components/ui/Modal';

const API_REIMB = `${API_V1}/expenses`;       // unified list + reimb actions
const API_VENDOR = `${API_V1}/payments`;      // legacy AP actions (NACHA, create, approval)

interface PaymentRunItem {
  beneficiary: string; vendor_id: string; amount: number;
  status: string; reports: string;
  // Bank fields: populated for both REIMBURSEMENT (D108 employee
  // bank link) and VENDOR (vendor master bank fields, 2026-05-19).
  employee_bank_account_id?: number | null;
  bank_name?: string | null;
  bank_account_last4?: string | null;
  payment_method?: string | null;
  // VENDOR-specific
  invoice_number?: string | null;
  invoice_id?: number | null;
  vendor_name?: string | null;
}

interface PaymentRun {
  id: number; entity_code: string; run_date: string; status: string;
  source: 'VENDOR' | 'REIMBURSEMENT' | 'MIXED';
  payment_method: string; payment_count: number; total_amount: number;
  executed_at?: string | null;
  items: PaymentRunItem[];
}

interface PayEntity { entity_code: string; entity_name: string; functional_currency: string; posted_invoices: number; }
interface PreviewInvoice { id: number; invoice_number: string; vendor_name: string; gross_amount: number; due_date: string; }

const SOURCE_OPTIONS = ['ALL', 'VENDOR', 'REIMBURSEMENT', 'MIXED'] as const;
type SourceFilter = typeof SOURCE_OPTIONS[number];

export default function PaymentRunsUnified() {
  const [runs, setRuns] = useState<PaymentRun[] | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  // New-vendor-run modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ entity_code: '', due_date_cutoff: '', payment_method: 'ACH' });
  const [entities, setEntities] = useState<PayEntity[]>([]);
  const [preview, setPreview] = useState<{ count: number; total_amount: number; invoices: PreviewInvoice[] } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const formatErr = (e: any): string => {
    if (!e) return 'Error';
    if (typeof e.detail === 'string') return e.detail;
    if (Array.isArray(e.detail)) {
      return e.detail.map((d: any) => `${(d.loc || []).slice(1).join('.')}: ${d.msg || d.type}`).join('; ') || 'Validation error';
    }
    if (typeof e === 'string') return e;
    try { return JSON.stringify(e).slice(0, 200); } catch { return 'Error'; }
  };

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API_REIMB}/payment-runs`);
      if (res.ok) { const d = await res.json(); setRuns(d.runs || []); }
      else { const e = await res.json().catch(() => ({})); showToast(formatErr(e), false); setRuns([]); }
    } catch (e) { console.error(e); setRuns([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Reimbursement execute (PENDING -> COMPLETED, posts PAYRUN JE) ──
  const handleExecute = async (runId: number, source: PaymentRun['source']) => {
    const url = source === 'VENDOR'
      ? `${API_VENDOR}/runs/${runId}/execute`
      : `${API_REIMB}/payment-runs/${runId}/execute`;
    try {
      const res = await authFetch(url, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        const ccy = d.currency || 'USD';
        const amt = d.total_amount != null ? Number(d.total_amount).toLocaleString() : '';
        showToast(`Run ${runId} executed -- ${ccy} ${amt} disbursed`, true);
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(formatErr(e), false);
      }
    } catch (err) { showToast(formatErr(err), false); }
  };

  // ── Vendor approval workflow ──
  const handleSubmitApproval = async (runId: number) => {
    const res = await authFetch(`${API_VENDOR}/runs/${runId}/submit-approval`, { method: 'POST' });
    if (res.ok) { showToast(`Run ${runId} submitted for approval`, true); load(); }
    else { const e = await res.json().catch(() => ({})); showToast(formatErr(e), false); }
  };
  const handleApprove = async (runId: number) => {
    const res = await authFetch(`${API_VENDOR}/runs/${runId}/approve`, { method: 'POST' });
    if (res.ok) { showToast(`Run ${runId} approved`, true); load(); }
    else { const e = await res.json().catch(() => ({})); showToast(formatErr(e), false); }
  };

  // ── NACHA download (vendor only) ──
  const handleNacha = async (runId: number) => {
    try {
      const res = await authFetch(`${API_VENDOR}/runs/${runId}/nacha`);
      if (!res.ok) { showToast('NACHA download failed', false); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `payment-run-${runId}-nacha.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { showToast(formatErr(e), false); }
  };

  // ── Vendor "+ New Run" modal ──
  const openCreate = async () => {
    setShowCreate(true);
    setPreview(null);
    setCreateForm({ entity_code: '', due_date_cutoff: '', payment_method: 'ACH' });
    try {
      const res = await authFetch(`${API_VENDOR}/entities`);
      if (res.ok) setEntities(await res.json());
    } catch (e) { console.error(e); }
  };
  const previewRun = async () => {
    if (!createForm.entity_code) return;
    setLoadingPreview(true);
    const p = new URLSearchParams({ entity_code: createForm.entity_code });
    if (createForm.due_date_cutoff) p.set('due_date_cutoff', createForm.due_date_cutoff);
    try {
      const res = await authFetch(`${API_VENDOR}/runs/preview?${p}`);
      if (res.ok) setPreview(await res.json());
      else { const e = await res.json().catch(() => ({})); showToast(formatErr(e), false); }
    } catch (e) { showToast(formatErr(e), false); }
    setLoadingPreview(false);
  };
  const createRun = async () => {
    try {
      const res = await authFetch(`${API_VENDOR}/runs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm),
      });
      if (res.ok) {
        showToast('Vendor payment run created', true);
        setShowCreate(false); load();
      } else { const e = await res.json().catch(() => ({})); showToast(formatErr(e), false); }
    } catch (e) { showToast(formatErr(e), false); }
  };

  // ── Filtering + KPIs ──
  const filtered = (runs || []).filter(r =>
    (sourceFilter === 'ALL' || r.source === sourceFilter)
    && (!statusFilter || r.status === statusFilter)
  );
  const pendingRuns = (runs || []).filter(r => r.status === 'PENDING' || r.status === 'DRAFT' || r.status === 'PENDING_APPROVAL' || r.status === 'APPROVED');
  const pendingTotal = pendingRuns.reduce((s, r) => s + (r.total_amount || 0), 0);
  const completedCount = (runs || []).filter(r => r.status === 'COMPLETED').length;
  const totalPaid = (runs || []).filter(r => r.status === 'COMPLETED').reduce((s, r) => s + (r.total_amount || 0), 0);

  return (
    <PageLayout
      title="Payment Runs"
      subtitle="All platform payment runs -- vendor AP + employee reimbursements. Execute to disburse to the bank."
      actions={[{ label: '+ New Vendor Payment Run', onClick: openCreate }]}
    >
      <KpiGrid items={[
        { label: 'Total Runs', value: runs?.length ?? 0 },
        { label: 'Pending', value: pendingRuns.length, color: pendingRuns.length > 0 ? 'var(--warning)' : 'var(--text-secondary)' },
        { label: 'Pending Total', value: formatAmountCompact(pendingTotal), color: 'var(--accent)' },
        { label: 'Completed', value: completedCount, color: 'var(--positive)' },
        { label: 'Total Paid', value: formatAmountCompact(totalPaid) },
      ]} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '12px 0' }}>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 6, alignItems: 'center' }}>
          Source
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as SourceFilter)} aria-label="Source filter"
            style={{ padding: '6px 10px', borderRadius: 6, fontSize: 12, border: '1px solid var(--border-default)' }}>
            {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 6, alignItems: 'center' }}>
          Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status filter"
            style={{ padding: '6px 10px', borderRadius: 6, fontSize: 12, border: '1px solid var(--border-default)' }}>
            <option value="">All</option>
            <option value="DRAFT">DRAFT</option>
            <option value="PENDING">PENDING</option>
            <option value="PENDING_APPROVAL">PENDING_APPROVAL</option>
            <option value="APPROVED">APPROVED</option>
            <option value="COMPLETED">COMPLETED</option>
            <option value="FAILED">FAILED</option>
          </select>
        </label>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-hint)' }}>
          Showing {filtered.length} of {runs?.length ?? 0}
        </span>
      </div>

      {runs === null ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-hint)' }}>
          No payment runs match the current filters.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((run) => (
            <div key={run.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, background: 'var(--bg-card)', padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, color: 'var(--accent)' }}>Run #{run.id}</span>
                <StatusBadge status={run.status} />
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                  background: run.source === 'VENDOR' ? '#dbeafe' : run.source === 'REIMBURSEMENT' ? '#dcfce7' : '#fef3c7',
                  color: run.source === 'VENDOR' ? '#1e40af' : run.source === 'REIMBURSEMENT' ? '#166534' : '#92400e' }}>
                  {run.source}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Entity {run.entity_code}</span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{run.run_date}</span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{run.payment_method}</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{run.payment_count} payment(s) · {formatAmount(run.total_amount)}</span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  {run.status === 'DRAFT' && run.source === 'VENDOR' && (
                    <ActionButton label="Submit for approval" onClick={() => handleSubmitApproval(run.id)} variant="secondary" size="sm" />
                  )}
                  {run.status === 'PENDING_APPROVAL' && run.source === 'VENDOR' && (
                    <ActionButton label="Approve" onClick={() => handleApprove(run.id)} variant="success" size="sm" />
                  )}
                  {(run.status === 'PENDING' || run.status === 'APPROVED') && (
                    <ActionButton label="Execute (disburse)" onClick={() => handleExecute(run.id, run.source)} variant="success" size="sm" />
                  )}
                  {run.status === 'COMPLETED' && run.source === 'VENDOR' && (
                    <ActionButton label="Download NACHA" onClick={() => handleNacha(run.id)} variant="secondary" size="sm" />
                  )}
                  {run.status === 'COMPLETED' && (
                    <span style={{ fontSize: 11, color: 'var(--positive)' }}>Executed {run.executed_at ? new Date(run.executed_at).toLocaleString() : ''}</span>
                  )}
                </div>
              </div>
              {run.items.length > 0 && (
                <table style={{ width: '100%', marginTop: 10, fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <th style={{ textAlign: 'left', padding: 4, fontSize: 10, color: 'var(--text-hint)' }}>BENEFICIARY</th>
                      <th style={{ textAlign: 'left', padding: 4, fontSize: 10, color: 'var(--text-hint)' }}>BANK</th>
                      <th style={{ textAlign: 'left', padding: 4, fontSize: 10, color: 'var(--text-hint)' }}>{run.source === 'VENDOR' ? 'INVOICE' : 'REPORTS'}</th>
                      <th style={{ textAlign: 'right', padding: 4, fontSize: 10, color: 'var(--text-hint)' }}>AMOUNT</th>
                      <th style={{ textAlign: 'left', padding: 4, fontSize: 10, color: 'var(--text-hint)' }}>STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {run.items.map((it, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: 4 }}>{it.beneficiary || it.vendor_id || '-'}</td>
                        <td style={{ padding: 4, fontFamily: 'monospace', fontSize: 11 }}>
                          {it.bank_account_last4
                            ? <>{it.bank_name || 'Bank'} ····{it.bank_account_last4}{it.payment_method && <span style={{ color: 'var(--text-hint)', marginLeft: 6 }}>· {it.payment_method}</span>}</>
                            : <span style={{ color: '#991b1b' }}>no bank on file</span>}
                        </td>
                        <td style={{ padding: 4, color: 'var(--text-secondary)' }}>
                          {run.source === 'VENDOR'
                            ? (it.invoice_number || '-')
                            : (it.reports || '-')}
                        </td>
                        <td style={{ padding: 4, textAlign: 'right', fontWeight: 600 }}>{formatAmount(it.amount)}</td>
                        <td style={{ padding: 4, fontSize: 10, color: it.status === 'SETTLED' ? 'var(--positive)' : 'var(--text-secondary)' }}>{it.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 10, color: 'var(--text-hint)' }}>
        Reimbursement runs post DR Cash-in-Transit / CR Cash on execute -- the actual bank disbursement.
        GL Cash moves here, not at batch time, so bank reconciliation ties out.
      </div>

      {showCreate && (
        <Modal open={showCreate} title="New Vendor Payment Run" onClose={() => setShowCreate(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>Entity</span>
              <select value={createForm.entity_code} onChange={(e) => { setCreateForm({ ...createForm, entity_code: e.target.value }); setPreview(null); }}
                aria-label="Entity"
                style={{ padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border-default)' }}>
                <option value="">Select entity...</option>
                {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} -- {e.entity_name} ({e.posted_invoices} posted invoices)</option>)}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>Due-date cutoff (optional)</span>
              <input type="date" value={createForm.due_date_cutoff} onChange={(e) => { setCreateForm({ ...createForm, due_date_cutoff: e.target.value }); setPreview(null); }}
                style={{ padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border-default)' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>Payment method</span>
              <select value={createForm.payment_method} onChange={(e) => setCreateForm({ ...createForm, payment_method: e.target.value })} aria-label="Payment method"
                style={{ padding: '8px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border-default)' }}>
                <option value="ACH">ACH</option>
                <option value="WIRE">WIRE</option>
                <option value="CHECK">CHECK</option>
              </select>
            </label>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button onClick={previewRun} disabled={!createForm.entity_code || loadingPreview}
                style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: loadingPreview ? 'wait' : 'pointer' }}>
                {loadingPreview ? 'Loading…' : 'Preview eligible invoices'}
              </button>
              {preview && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{preview.count} invoices · {formatAmount(preview.total_amount)}</span>
              )}
            </div>
            {preview && preview.invoices.length > 0 && (
              <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-subtle)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>INVOICE</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>VENDOR</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px' }}>DUE</th>
                      <th style={{ textAlign: 'right', padding: '6px 8px' }}>AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.invoices.map(inv => (
                      <tr key={inv.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '6px 8px' }}>{inv.invoice_number}</td>
                        <td style={{ padding: '6px 8px' }}>{inv.vendor_name}</td>
                        <td style={{ padding: '6px 8px' }}>{inv.due_date || '-'}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{formatAmount(inv.gross_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
              <button onClick={() => setShowCreate(false)}
                style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={createRun} disabled={!preview || preview.count === 0}
                style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: (!preview || preview.count === 0) ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: (!preview || preview.count === 0) ? 'not-allowed' : 'pointer' }}>
                Create payment run
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
